// @ts-strict-ignore
import { ArticleTechnicalInfoBundle, ArticleTechnicalInfoVO, ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { Dictionary } from 'typescript-collections';
import { CompressionSimpleTextValueVO, SimpleTextValueVO, TextureAreaUsageVO, TextureAreaVO } from '@shared/vo/meta-element.vo';
import { PropertyUtils } from '@article/util/property-utils';
import { MetaElementBehaviorPropertyDef } from '@project/constants/meta-element-behavior-property-def';
import { StringUtils } from '@shared/util/string-utils';
import { TextSizeCheckRequestVO } from '@3d-scene/manager/text-size-check.vo';
import { TextSizeCheckManager } from '@3d-scene/manager/text-size-check.manager';
import { ArrayUtils } from '@shared/util/array-utils';
import { ISpanData, SpanDataFactory } from '@project/components/label/label-editor/span-data';
import { LabelFormat } from '@project/components/label/label-editor/label-format';
import { ProjectModel } from '@project/model/project.model';
import { ArticleConnectionPropertyDef } from '@article/constants/article-connection-property-def';
import { MetaElementUsageUtils } from '@project/util/meta-element-usage-utils';
import { ITableProvider, TableProvider, TableProviderType } from '@project/components/label/label-editor/table-provider';
import { ArticleTechnicalInfoManager } from '@article/manager/article-technical-info.manager';
import { MarkingSystemDataService } from '@project/components/article/manager/marking-system-data.service';
import { PropertyInLocalesVO } from '@project/components/article/model/vo/property.vo';
import { ArticleTechnicalInfoPropertyDef } from '@article/constants/article-technical-info-property';
import { SelectionModel } from '@project/components/article/model/selection.model';
import { RepresentationVO } from '@shared/vo/representation.vo';
import { ArticleManager } from '@article/manager/article.manager';
import { UndoRedoManager } from '@shared/undo/undo-redo.manager';
import { LoadConnectionTextureProcessor } from '@3d-scene/processor/load-connection-texture.processor';
import { AlertHelper } from '@shared/alert/alert.helper';
import { TextureUtils } from '@3d-scene/model3d/util/texture-utils';
import { IOperation } from '@shared/undo/operation/i-operation';
import { TextureConnectionInfo } from '@3d-scene/model3d/texture-connection-info';
import { Connection } from '@3d-scene/model3d/connection';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { AsideLabelEditorData, LabelEditorApplyData, LabelEditorChanges } from '../aside-label-editor/aside-label-editor.component';
import { LabelEditorSequenceOperation } from '@shared/undo/operation/label-editor-sequence-operation';
import { LabelEditorMode } from '../label-editor.component';
import { UnusedLabelingService } from './unused-labeling.service';
import { LabelEditorStateManager } from './label-editor-state.manager';
import { Injectable } from '@angular/core';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { LabelingUtils } from '@3d-scene/model3d/util/labeling-utils';
import { ArticleTechnicalInfoService } from '@article/service/article-technical-info.service';
import { Log } from '@shared/debug/log';
import { SelectionModelService } from '../../../article/service/selection-model.service';
import { ProjectLabelEditorSelectionModelService } from '../../../article/service/project-label-editor-selection-model.service';
import { UpdateLabelingCompressionProcessor } from '@3d-scene/processor/update-labeling-compression.processor';
import { MarkingSystemProjectCreationServiceDelegator } from '@configuration/wizard/service/marking-system-project-creation-service.delegator';
import { MarkingCardArticleService } from './marking-card-article.service';
import { ProjectUtils } from '../../../project/util/project-utils';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';

export interface MarkingSystemLabelEditorData {
  isNewProject: boolean;
  articleTechnicalInfoBundles: ArticleTechnicalInfoBundle[] | null;
  article: ArticleVO;
  colorString: string;
}

@Injectable({ providedIn: 'root' })
export class OpenMarkingSystemLabelEditorService {

  public articleTechnicalInfoBundles: ArticleTechnicalInfoBundle[];
  public colorString: string;

  private article: ArticleVO;
  private relatedArticles: ArticleVO[];
  private selectedArticle: ArticleVO;
  private connectionTextSizeCheckClasses: Dictionary<string, string> = new Dictionary();
  private isNewProject: boolean;

  constructor(
    private readonly markingCardArticleService: MarkingCardArticleService,
    private readonly textSizeCheckManager: TextSizeCheckManager,
    private readonly articleTechnicalInfoManager: ArticleTechnicalInfoManager,
    private readonly projectModel: ProjectModel,
    private readonly markingSystemDataService: MarkingSystemDataService,
    private readonly selectionModel: SelectionModel,
    private readonly selectionModelService: SelectionModelService,
    private readonly projectLabelEditorSelectionModelService: ProjectLabelEditorSelectionModelService,
    private readonly articleManager: ArticleManager,
    private readonly undoRedoManager: UndoRedoManager,
    private readonly loadConnectionTextureProcessor: LoadConnectionTextureProcessor,
    private readonly alertHelper: AlertHelper,
    private readonly unusedLabelingService: UnusedLabelingService,
    private readonly labelEditorStateManager: LabelEditorStateManager,
    private readonly containerService: ContainerService,
    private readonly connectionInfoService: ConnectionInfoService,
    private readonly labelingUtils: LabelingUtils,
    private readonly articleTechnicalInfoService: ArticleTechnicalInfoService,
    private readonly markingSystemProjectCreationServiceDelegator: MarkingSystemProjectCreationServiceDelegator,
    private readonly updateLabelingCompressionProcessor: UpdateLabelingCompressionProcessor,
    private readonly spanDataFactory: SpanDataFactory,
  ) {
  }

  public async openMarkingSystemLabelEditorHandler(data: MarkingSystemLabelEditorData): Promise<void> {
    this.isNewProject = data.isNewProject;
    if (data.isNewProject) {
      this.articleTechnicalInfoBundles = data.articleTechnicalInfoBundles;
      this.article = data.article;
      this.colorString = data.colorString;
      if (this.colorString && this.articleTechnicalInfoBundles != null) {
        await this.continueExecution();
      } else {
        const articles: ArticleVO[] = await this.markingSystemDataService.getRelatedArticles(this.article.identity).toPromise();
        await this.loadTechnicalInfoByArticles(articles);
      }
    } else {
      if (this.article && this.article.identity === data.article.identity && this.colorString && this.articleTechnicalInfoBundles) {
        await this.continueExecution();
      } else {
        this.article = data.article;
        const articles: ArticleVO[] = await this.markingSystemDataService.getRelatedArticles(this.article.identity).toPromise();
        await this.loadTechnicalInfoByArticles(articles);
      }
    }
  }

  public async applyLabelEditorChangesHandler(data: LabelEditorApplyData): Promise<void> {
    if (data.markingSystemLabelModeEnabled) {
      await this.applyLabelEditorChanges(this.article, data.changes);
    }
  }

  private async continueExecution(): Promise<void> {
    const textSizeCheckRequests: any[] = [];
    if (this.article != null && this.article.metaElements != null) {
      for (const metaElementVO of this.article.metaElements) {
        if (metaElementVO instanceof TextureAreaVO) {
          const textSizeCheckClass: string = PropertyUtils.getStringPropertyValue(
            metaElementVO.behaviorProperties,
            MetaElementBehaviorPropertyDef.TEXT_CHECK_CLASS
          );
          if (StringUtils.isNotEmpty(textSizeCheckClass)) {
            const req: TextSizeCheckRequestVO = new TextSizeCheckRequestVO();
            req.textSizeCheckClass = textSizeCheckClass;
            if (metaElementVO.corridor) {
              const key: string = metaElementVO.corridor.toString();
              this.connectionTextSizeCheckClasses[key] = textSizeCheckClass;
            }
            if (this.textSizeCheckManager.isRetrievalNecessary(req)) {
              textSizeCheckRequests.push(req);
            }
          }
        }
      }
    }
    if (ArrayUtils.isNotEmpty(textSizeCheckRequests)) {
      const success = await this.textSizeCheckManager.retrieveTextSizeCheckInfos(textSizeCheckRequests);
      if (success) {
        this.initiateAndShowLabelEditorPopup();
      }
    } else {
      this.initiateAndShowLabelEditorPopup();
    }
  }

  private async loadTechnicalInfoByArticles(articles: ArticleVO[]): Promise<void> {
    const articleIds: number[]  = [];
    this.relatedArticles = articles;
    for (const article of articles) {
      const articleId: number = article.identity;
      const technicalInfo: ArticleTechnicalInfoVO = this.articleTechnicalInfoManager.getTechnicalInfo(articleId);
      if (technicalInfo == null) {
        articleIds.push(articleId);
      }
    }
    if (ArrayUtils.isNotEmpty(articleIds)) {
      try {
        const arrayResponse: ArticleTechnicalInfoVO[] = await this.articleTechnicalInfoManager.loadTechnicalInfoByArticleIdsObservable(articleIds, true).toPromise();
        await this.technicalInfoLoaded(arrayResponse);
      } catch (error) {
        Log.error('Could not load technical info: ', error);
        this.articleTechnicalInfoService.notifyArticleTechnicalInfoLoadedFailure(error)
      }
    } else {
      await this.technicalInfoLoaded();
    }
  }

  private async technicalInfoLoaded(info: ArticleTechnicalInfoVO[] = null): Promise<void> {
    this.articleTechnicalInfoBundles = [];
    for (const relatedArticle of this.relatedArticles) {
      const techInfo: ArticleTechnicalInfoVO = this.articleTechnicalInfoManager.getTechnicalInfo(relatedArticle.identity);
      if (techInfo == null) {
        continue;
      }
      let property: PropertyInLocalesVO;
      const bundleObject: ArticleTechnicalInfoBundle = {
        articleVO: relatedArticle,
        articleTechnicalInfoVO: techInfo,
      };
      this.articleTechnicalInfoBundles.push(bundleObject);
      if (this.article.identity === relatedArticle.identity) {
        for (property of techInfo.articleTechnicalInfoVOs) {
          if (property.name === ArticleTechnicalInfoPropertyDef.COLOR) {
            for (const propValue of property.values) {
              if (propValue.locale === 'en_US' || propValue.locale === 'en_GB') {
                this.colorString = propValue.stringValue.toLocaleLowerCase();
              }
            }
          }
        }
      }
    }
    await this.continueExecution();
  }

  private initiateAndShowLabelEditorPopup(): void {
    const articleUsage: ArticleUsageVO = ProjectUtils.getFirstArticleUsage(this.projectModel.currentProject);
    const spacing: number = PropertyUtils.getFloatPropertyValue(this.article.connectionRequirements, ArticleConnectionPropertyDef.SPACING);
    const spanData: ISpanData[][] = [];
    for (let i: number = 0; i < 19; i++) {
      const row: ISpanData[] = [];
      spanData[i] = row;
    }

    for (const textureAreaUsage of ArticleUsageUtils.textureAreaUsages(articleUsage)) {
      let iPos: number = 0;
      let jPos: number = 0;
      const corridor: number = MetaElementUsageUtils.getCorridorInformationOfTextureArea(textureAreaUsage, this.article);
      let labelData: string = '';

      if (!isNaN(corridor)) {

        const labelPos: number = corridor;
        if (!(textureAreaUsage.textValue instanceof CompressionSimpleTextValueVO)) {
          if (textureAreaUsage.textValue instanceof SimpleTextValueVO) {
            const simpleTextValue: SimpleTextValueVO = textureAreaUsage.textValue as SimpleTextValueVO;
            const compressionTextValue: CompressionSimpleTextValueVO = new CompressionSimpleTextValueVO();
            compressionTextValue.text = simpleTextValue.text;
            compressionTextValue.rotation = simpleTextValue.rotation;
            compressionTextValue.hAlignment = simpleTextValue.hAlignment;
            compressionTextValue.vAlignment = simpleTextValue.vAlignment;
            compressionTextValue.bgColor = simpleTextValue.bgColor;
            textureAreaUsage.textValue = compressionTextValue;
          } else {
            const compressionTextValue: CompressionSimpleTextValueVO = new CompressionSimpleTextValueVO();
            textureAreaUsage.textValue = compressionTextValue;
          }
        }
        this.populateSpanData(textureAreaUsage, labelData, labelPos, iPos, jPos, spacing, spanData);
      }
    }

    const tableProvider: ITableProvider = new TableProvider(this.containerService, TableProviderType.MARKING_CARD, spanData);
    const labelEditorTargetState: string =
      this.projectModel.user != null && this.projectModel.user.hasPermission("USE_ADVANCED_TEXT_EDIT_FUNCTIONS")
        ? 'advancedControl'
        : 'basicControl';
    const asideLabelEditorData: AsideLabelEditorData = {
      tableProviders: [tableProvider],
      labelEditorTargetState: labelEditorTargetState,
      showDefaultLabelingBorderExplanation: false,
      markingSystemLabelModeEnabled: true,
      articleTechnicalInfoBundles: this.articleTechnicalInfoBundles,
      selectedColorString: this.colorString,
      selectAllCellsOnInit: this.isNewProject,
    };
    this.labelEditorStateManager.openAsideLabelEditor(asideLabelEditorData);
  }

  private populateSpanData(textureAreaUsage: TextureAreaUsageVO, labelData: string, labelPos: number, iPos: number, jPos: number, spacing: number, spanData: ISpanData[][]) {
    let labelHeight: number = LabelFormat.DEFAULT_CELL_HEIGHT;
    let labelWidth: number = LabelFormat.DEFAULT_CELL_WIDTH;
    const connection: Connection = this.connectionInfoService.findConnectionByIdentity(textureAreaUsage.identity);
    const articleContainer: ArticleContainer = connection.articleContainer;
    const textureInfo: TextureConnectionInfo = articleContainer.findTextureConnectionInfo(connection.connectionName);
    if (textureInfo) {
      labelHeight = textureInfo.connectionHeight;
      labelWidth = textureInfo.connectionWidth;
    }
    labelData = (textureAreaUsage.textValue as CompressionSimpleTextValueVO).text;
    const vAlignment: string = TextureUtils.getVerticalAlignByIndex(
      (textureAreaUsage.textValue as CompressionSimpleTextValueVO).vAlignment
    );

    if (labelPos <= 0 || labelPos > 100) return;

    iPos = Math.floor((labelPos - 1) / 10);
    jPos = labelPos - iPos * 10 - 1;
    let cellLabelSizeInfos: any[] = null;
    let textSizeCheckClass: string;
    const cnName: string = ((iPos / 2) * 10 + jPos + 1).toString();
    if (this.connectionTextSizeCheckClasses[cnName]) {
      cellLabelSizeInfos = this.textSizeCheckManager.getTextSizeCheck(this.connectionTextSizeCheckClasses[cnName], spacing);
      textSizeCheckClass = this.connectionTextSizeCheckClasses[cnName];
    }
    spanData[iPos * 2][jPos] = this.spanDataFactory.createSpanData(
      labelData,
      new LabelFormat(
        '#FFFFFF',
        (textureAreaUsage.textValue as CompressionSimpleTextValueVO).rotation,
        vAlignment,
        null,
        labelWidth,
        labelHeight
      ),
      cellLabelSizeInfos
    );
    spanData[iPos * 2][jPos].textCheckClass = textSizeCheckClass;
    spanData[iPos * 2][jPos].compression = (textureAreaUsage.textValue as CompressionSimpleTextValueVO).compression;
    spanData[iPos * 2][jPos].autoFormatByTextCheck =
      (textureAreaUsage.textValue as CompressionSimpleTextValueVO).autoFontSize ||
      (textureAreaUsage.textValue as CompressionSimpleTextValueVO).autoCompression;
    spanData[iPos * 2][jPos].enabled = true;
    if (iPos <= 8) {
      // disabled cells for separation lines:
      spanData[iPos * 2 + 1][jPos] = this.spanDataFactory.createSpanData(
        null,
        new LabelFormat(null, null, null, null, labelWidth, labelHeight / 2),
      ); // we use half of the height of editable cells here
      spanData[iPos * 2 + 1][jPos].enabled = false;
    }
  }

  private async applyLabelEditorChanges(article: ArticleVO, changes: LabelEditorChanges): Promise<void> {
    this.selectedArticle = changes.selectedArticle;
    const articleUsage: ArticleUsageVO = ProjectUtils.getFirstArticleUsage(this.projectModel.currentProject);
    if (this.selectedArticle.identity !== articleUsage.articleId) {
      const markerCardArticle: ArticleVO = await this.loadMarkerCardArticle(this.selectedArticle);
      if (markerCardArticle != null) {
        await this.executeReplaceArticleUsage(this.selectedArticle, markerCardArticle);
      }
    } else {
      // TODO Was war hier drin?
      const operations = this.markingCardArticleService.saveLabeling(articleUsage, article, changes);
      await this.replaceSucceeded(operations, changes);
    }
  }

  private async loadMarkerCardArticle(article: ArticleVO): Promise<ArticleVO> {
    if (article == null) {
      return;
    }
    let markerCardArticleId: number = NaN;
    let markerCardArticleVersion: number = NaN;
    for (const rep of article.representations) {
      if (rep.type === RepresentationVO.TYPE_ARTICLE && rep.name === 'MARKER_CARD') {
        markerCardArticleId = rep.repArticleId;
        markerCardArticleVersion = rep.repArticleVersion;
      }
    }
    if (isNaN(markerCardArticleId) || isNaN(markerCardArticleVersion)) {
      return;
    }
    const fullArticle: ArticleVO = this.articleManager.getCachedArticle(markerCardArticleId, markerCardArticleVersion);
    if (fullArticle != null) {
      return fullArticle;
    }
    const art = await this.articleManager.loadFullArticle(markerCardArticleId, markerCardArticleVersion).toPromise();
    await this.executeReplaceArticleUsage(this.selectedArticle, art);
  }

  private async executeReplaceArticleUsage(selectedArticle: ArticleVO, markerCardArticle: ArticleVO) {
    let replaceArticleResult = await this.markingCardArticleService.executeReplaceArticleUsage(selectedArticle, markerCardArticle, this.article);
    if (replaceArticleResult.success) {
      await this.replaceSucceeded(replaceArticleResult.operations);
    } else {
      this.replaceFailed();
    }
  }

  private async replaceSucceeded(operations: IOperation[], changes: LabelEditorChanges = null): Promise<void> {
    this.pushOperations(operations, changes);
    await this.markingSystemProjectCreationServiceDelegator.updateMarkingSystemPredefinedAreas();
    this.projectModel.notifyCurrentProjectChanged();
    await this.updateLabelingCompressionProcessor.updateTextures();
    this.setInEditingMode();
  }

  private replaceFailed(): void {
    this.setInEditingMode();
    this.alertHelper.showError('global.title.error', 'markingSystem.errorMessage.savingModelFailed');
  }

  private setInEditingMode() {
    this.projectLabelEditorSelectionModelService.labelEditorModeChanged(LabelEditorMode.EDITING);
    this.unusedLabelingService.labelEditorModeChangedHandler(LabelEditorMode.EDITING);
    this.labelEditorStateManager.changeLabelEditorMode(LabelEditorMode.EDITING);
  }

  private pushOperations(operations: IOperation[], changes: LabelEditorChanges): void {
    if (this.undoRedoManager != null && ArrayUtils.isNotEmpty(operations)) {
      const tableProviderIdHandsontableOperationsMap = new Dictionary<string, any[]>();
      changes.tableProviderHandsontableOperationsMap.forEach((handsontableOperations: any[], tableProvider: ITableProvider) => {
        tableProviderIdHandsontableOperationsMap.setValue(tableProvider.id, handsontableOperations);
      });
      this.undoRedoManager.pushUndo(
        new LabelEditorSequenceOperation(
          operations,
          this.selectionModel,
          this.selectionModelService,
          this.labelEditorStateManager,
          this.connectionInfoService,
          this.containerService,
          this.labelingUtils,
          null,
          tableProviderIdHandsontableOperationsMap,
          this.loadConnectionTextureProcessor,
          this.updateLabelingCompressionProcessor,
          null,
          this.projectModel,
          true
        )
      );
    }
  }
}
